import { useEffect } from 'react';
import { Star, Users, BookOpen } from 'lucide-react';
import HeroSection from '../components/layout/HeroSection';
import CourseRecommendationSection from '../components/layout/CourseRecommendationSection';
import LearningPathSection from '../components/layout/LearningPathSection';
import TestimonialsSection from '../components/layout/TestimonialsSection';
import TrustedCompaniesSection from '../components/layout/TrustedCompaniesSection';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import '../styles/scrollAnimations.css';

const HomePage = () => {
  const stats = [
    { label: 'Active Students', value: '370,000+', icon: Users },
    { label: 'Courses Available', value: '40+', icon: BookOpen },
    { label: 'Average Rating', value: '4.8/5', icon: Star },
  ];

  // Optimized scroll animation hooks - reduced number for better performance
  const { ref: heroRef, isVisible: heroVisible } = useScrollAnimation({ threshold: 0.01, delay: 0, rootMargin: '50px' });
  const { ref: recommendationRef, isVisible: recommendationVisible } = useScrollAnimation({ threshold: 0.05, delay: 0, rootMargin: '0px 0px -20px 0px' });
  const { ref: learningPathRef, isVisible: learningPathVisible } = useScrollAnimation({ threshold: 0.05, delay: 100, rootMargin: '0px 0px -20px 0px' });
  const { ref: testimonialsRef, isVisible: testimonialsVisible } = useScrollAnimation({ threshold: 0.05, delay: 150, rootMargin: '0px 0px -20px 0px' });
  const { ref: statsRef, isVisible: statsVisible } = useScrollAnimation({ threshold: 0.05, delay: 0, rootMargin: '0px 0px -20px 0px' });

  // Mark page as loaded for scroll optimization
  useEffect(() => {
    const timer = setTimeout(() => {
      document.body.classList.add('loaded');
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="overflow-x-hidden scroll-container">
      {/* Modern Hero Section */}
      <div 
        ref={heroRef}
        className={`hero-scroll-fade scroll-section ${heroVisible ? 'animate' : ''}`}
      >
        <HeroSection />
      </div>

      {/* Course Recommendation Section */}
      <div 
        ref={recommendationRef}
        className={`section-reveal scroll-section ${recommendationVisible ? 'animate' : ''}`}
        id="course-recommendations"
      >
        <CourseRecommendationSection />
      </div>

      {/* Learning Path Section */}
      <div 
        ref={learningPathRef}
        className={`section-reveal scroll-section ${learningPathVisible ? 'animate' : ''}`}
      >
        <LearningPathSection id="individual-courses" />
      </div>

      {/* Testimonials Section */}
      <div 
        ref={testimonialsRef}
        className={`section-reveal scroll-section ${testimonialsVisible ? 'animate' : ''}`}
      >
        <TestimonialsSection />
      </div>

      <div className="section-padding-lg space-y-16 sm:space-y-20 lg:space-y-24">
        {/* Stats Section - Enhanced with optimized scroll animations */}
        <section 
          ref={statsRef}
          className={`container section-reveal scroll-section ${statsVisible ? 'animate' : ''}`}
          id="stats-section"
        >
          <div className={`text-center mb-12 sm:mb-16 text-reveal ${statsVisible ? 'animate' : ''}`}>
            <h2 className="text-responsive-3xl font-bold text-gray-900 mb-4">
              Trusted by <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">Thousands of Learners</span>
            </h2>
            <p className="text-responsive-lg text-secondary-600 max-w-3xl mx-auto">
              Join our community of successful professionals who have transformed their careers through our courses from Udemy, YouTube and corporate training.
            </p>
          </div>
          
          <div className={`cards-container ${statsVisible ? 'animate' : ''} grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 max-w-5xl mx-auto`}>
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div
                  key={index}
                  className={`card-item group text-center p-6 sm:p-8 bg-white rounded-2xl shadow-lg border hover:shadow-2xl hover:shadow-green-500/10 transition-all duration-300 transform hover:-translate-y-2 gpu-accelerated ${
                    statsVisible ? 'animate' : ''
                  }`}
                  style={{ 
                    transitionDelay: statsVisible ? `${index * 100}ms` : '0ms',
                  }}
                >
                  <div className="w-12 h-12 sm:w-16 sm:w-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-6 group-hover:scale-110 transition-transform duration-200 shadow-lg shadow-green-500/20">
                    <Icon className="h-6 w-6 sm:h-8 sm:w-8 text-white" aria-label="Icon icon" />
                  </div>
                  <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-secondary-900 mb-2 sm:mb-3 group-hover:text-green-600 transition-colors duration-200">
                    {stat.value}
                  </div>
                  <div className="text-secondary-600 text-base sm:text-lg group-hover:text-green-700 transition-colors duration-200">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </section>

      </div>

      {/* Trusted Companies Section - No animation for better performance */}
      <div className="scroll-section">
        <TrustedCompaniesSection />
      </div>
    </div>
  );
};

export default HomePage;